import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminRevenue } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { cx } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/revenue")({
  component: RevenueScreen,
});

/**
 * The money at the scale of the installation.
 *
 * Billing was readable one organisation at a time, which answers "what does
 * this customer owe" and not the question the end of a month brings: what are
 * we about to invoice, what is overdue, and how much of it will never arrive.
 *
 * Every figure is derived from the rows the invoices are drawn from. Nothing is
 * kept beside them — a counter maintained alongside a ledger is a second answer
 * that can disagree with the first, and the one on the screen is always the one
 * that disagrees.
 */
function RevenueScreen() {
  const revenue = useQuery({
    queryKey: ["platform-revenue"],
    queryFn: ({ signal }) => api.get<AdminRevenue>("/admin/revenue", undefined, signal),
    refetchInterval: 60000,
  });

  if (revenue.isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (revenue.isError) {
    return <ErrorState error={revenue.error} onRetry={() => void revenue.refetch()} />;
  }

  const c = revenue.data!.credit;
  const creditMonths = revenue.data?.credit_months ?? [];
  const r = revenue.data!.receivables;
  const months = revenue.data?.months ?? [];
  const top = revenue.data?.top_accounts ?? [];
  const chased = isMoney(r.overdue) || isMoney(r.suspended) || isMoney(r.expiring);
  // Anything still owed on the old invoices. Shown only while there is: an
  // empty four-bucket card on every visit is a card somebody learns to
  // skip, and these go to zero as the last of them is settled.
  const legacy = chased || isMoney(r.not_yet_due) || isMoney(r.draft) || months.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {c.awaiting_document > 0 && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {c.awaiting_document === 1
              ? "A purchase of credit has been paid for and never invoiced"
              : `${c.awaiting_document} purchases of credit have been paid for and never invoiced`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            The invoice is drawn every quarter of an hour, so minutes here are
            normal and hours mean the bookkeeping system has been refusing them.
            The money is already in the bank, which makes this the kind of gap an
            auditor finds rather than a customer.
          </p>
        </div>
      )}

      {/* What is owed to customers, which is the figure this screen now leads
          with. Unspent credit is money in the bank that has not been earned,
          and its VAT has already been declared — an installation reading its
          bank balance as revenue would overstate it by exactly this. */}
      <Panel
        title="Credit"
        description="Capacity is paid for before it is used, so the question is not what is owed to us. Credit sold and not yet spent is a liability: money received, VAT declared, nothing earned."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Figure label="Owed to customers" value={c.outstanding} big />
          <Figure
            label="Sold this month"
            value={c.sold_this_month_net}
            hint={`${c.sold_this_month_gross} charged, ${c.sold_this_month_count} ${c.sold_this_month_count === 1 ? "purchase" : "purchases"}`}
          />
          <Figure
            label="Spent this month"
            value={c.spent_this_month}
            hint="Rent and plan changes: what becomes earned"
          />
          <Figure
            label="Started, not paid"
            value={c.pending_gross}
            hint={`${c.pending_count} ${c.pending_count === 1 ? "payment page" : "payment pages"}, mostly abandoned`}
          />
        </div>
      </Panel>

      <Panel
        title="Where the accounts stand"
        description="The figure worth acting on is the middle one: accounts about to stop, while there is still time to ask why."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Figure label="Renting" value={c.renting.toLocaleString()} big />
          <Figure
            label="Ending within a week"
            value={c.lapsing_soon.toLocaleString()}
            tone={c.lapsing_soon > 0 ? "warning" : undefined}
          />
          <Figure
            label="Stopped"
            value={c.lapsed.toLocaleString()}
            hint="Rental ran out; data kept"
            tone={c.lapsed > 0 ? "danger" : undefined}
          />
          <Figure
            label="Never rented"
            value={c.never_rented.toLocaleString()}
            hint="Signed up and never started"
          />
        </div>
      </Panel>

      <Panel
        title="Credit by month"
        description="By when the money arrived rather than when the purchase was started: a payment page opened in March and paid in April is April's sale, and April is the month its document is dated."
        padded={false}
      >
        {creditMonths.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No credit sold yet" description="Nothing has been bought." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[28%]">Month</Th>
                  <Th width="w-[12%]" numeric>
                    Purchases
                  </Th>
                  <Th width="w-[15%]" numeric>
                    Net
                  </Th>
                  <Th width="w-[15%]" numeric>
                    Tax
                  </Th>
                  <Th width="w-[15%]" numeric>
                    Gross
                  </Th>
                  <Th width="w-[15%]" numeric>
                    Spent
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {creditMonths.map((m) => (
                  <Tr key={m.period_start}>
                    <Td>{m.period}</Td>
                    <Td numeric>{m.purchases.toLocaleString()}</Td>
                    <Td numeric>{m.net}</Td>
                    <Td numeric>{m.tax}</Td>
                    <Td numeric>{m.gross}</Td>
                    <Td numeric>{m.spent}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      {(chased || r.stopped_accounts > 0) && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {r.stopped_accounts > 0
              ? `${r.stopped_accounts} ${r.stopped_accounts === 1 ? "account is" : "accounts are"} stopped over an unpaid invoice`
              : "Invoices are past their due date"}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            {r.expiring} belongs to accounts days away from having their services deleted,{" "}
            {r.suspended} to accounts already stopped, and {r.overdue} is past due with the
            services still running.
          </p>
        </div>
      )}

      {/* The old invoices, while any of them is open.
          "Accruing" is gone with them: metered hours carry no invoice and never
          will, so a figure describing them as money about to be billed would be
          describing something that cannot happen. The readings are still taken
          and are shown in each account, without a price beside them. */}
      {legacy && (
      <Panel
        title="Owed on the old invoices"
        description="Drawn for metered usage before capacity was rented. No further one is created, and these go to zero as the last of them is settled — by how far collection has got rather than by how late the money is, because each stage is a different conversation."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Figure label="Not yet due" value={r.not_yet_due} hint="Inside the 7-day term" />
          <Figure
            label="Past due"
            value={r.overdue}
            hint="Services still running"
            tone={isMoney(r.overdue) ? "warning" : undefined}
          />
          <Figure
            label="Services stopped"
            value={r.suspended}
            hint="Paying starts them again"
            tone={isMoney(r.suspended) ? "warning" : undefined}
          />
          <Figure
            label="Final notice sent"
            value={r.expiring}
            hint="Services deleted in days"
            tone={isMoney(r.expiring) ? "danger" : undefined}
          />
          <Figure label="Closed, not yet invoiced" value={r.draft} />
          <Figure label="Written off, all time" value={r.uncollectible} />
        </div>
      </Panel>
      )}

      <Panel
        title="Heaviest accounts this period"
        description="What is actually being used, from the meter. Not what it costs: capacity is rented by the month, so a busy account and a quiet one on the same plan pay the same. Exempt accounts are included and marked — the operator's own estate is usually among the largest, and leaving it out makes the figures look wrong."
        padded={false}
      >
        {top.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="Nothing metered yet" description="No account has used anything this period." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[70%]">Organisation</Th>
                  <Th width="w-[30%]" numeric>
                    Metered hours
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {top.map((a) => (
                  <Tr key={a.organisation_id}>
                    <Td clip title={a.organisation_name}>
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: a.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {a.organisation_name}
                      </Link>
                      {a.exempt && (
                        <Chip  className="ml-2">
                          not charged
                        </Chip>
                      )}
                    </Td>
                    <Td numeric>{a.hours.toLocaleString()}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel
        title="By month"
        description="By the period an invoice covers, not when it was drawn: an invoice for September drawn on the second of October belongs to September in every conversation anybody will have about it."
        padded={false}
      >
        {months.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No invoices yet" description="No month has been closed." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[22%]">Month</Th>
                  <Th width="w-[10%]" numeric>
                    Invoices
                  </Th>
                  <Th width="w-[14%]" numeric>
                    Net
                  </Th>
                  <Th width="w-[12%]" numeric>
                    Tax
                  </Th>
                  <Th width="w-[14%]" numeric>
                    Gross
                  </Th>
                  <Th width="w-[14%]" numeric>
                    Paid
                  </Th>
                  <Th width="w-[14%]" numeric>
                    Open
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <Tr key={m.period_start}>
                    <Td clip>{m.period}</Td>
                    <Td numeric>{m.invoices}</Td>
                    <Td numeric>{m.net}</Td>
                    <Td numeric>{m.tax}</Td>
                    <Td numeric>{m.gross}</Td>
                    <Td numeric>{m.paid}</Td>
                    <Td numeric>
                      {isMoney(m.open) ? (
                        <span className="text-[var(--warning)]">{m.open}</span>
                      ) : (
                        m.open
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/**
 * Whether an amount is anything at all.
 *
 * The API renders money as "0.00 EUR" rather than a number, so a truthiness
 * check on the string says yes to zero. This is what the colour and the banners
 * are decided by, and getting it wrong would paint a healthy installation red.
 */
function isMoney(amount: string): boolean {
  const digits = amount.replace(/[^\d]/g, "");
  return digits !== "" && Number(digits) > 0;
}

function Figure({
  label,
  value,
  hint,
  big,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  big?: boolean;
  tone?: "warning" | "danger";
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
        {label}
      </p>
      <p
        className={cx(
          "mt-1 font-semibold tabular-nums tracking-tight",
          big ? "text-2xl" : "text-lg",
          tone === "danger"
            ? "text-[var(--danger)]"
            : tone === "warning"
              ? "text-[var(--warning)]"
              : "text-[var(--text-primary)]",
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">{hint}</p>}
    </div>
  );
}
